import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useLocation, type NavigateOptions } from 'react-router-dom';
import { ArrowLeft, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ReleaseTourHelpMenu } from '@/shared/release-tour';
import { useBarCrumb, useBarTrail } from './bar-crumb';
import { HOME, useNavStack } from './nav-stack';
import { PRACTICE_IN_BAR } from './practice-in-bar';
import { PracticeSwitcher } from '@/scheduling/components/calendar/practice-switcher';
import { useAuthStore } from '@/auth/auth-store';
import { isNavConfined } from '@/app/lib/nav-confinement';
import { isPhlebotomistRole } from '@/auth/roles';
import type { NavId } from '@/app/lib/last-selected-nav';
import { dashboardNavLabel } from '@/app/lib/dashboard-nav-label';

/**
 * TopBar — the zone-contract GlobalBar (Z2) for the Practice app shell.
 *
 * 48px (`--zone-global-bar`), no fill of its own: it sits on the app grey over
 * the WorkspaceFrame. Left: a breadcrumb derived from the URL. Right (Antonin,
 * 12 Oct, both apps): Help, then a quiet patient search, `--search-width` wide
 * (shrinks to 200 first on narrow windows; the crumbs truncate after it): hint
 * placeholder that says what the search finds from this page, swapping under a
 * swipe of white light on every page change; ⌘K in a hairline outline focuses
 * it; Enter opens the patient list filtered by the query (passed as navigation state, not in the URL).
 *
 * Structure only. The breadcrumb reads the pathname and, for `/`, the shell's
 * `activeNav` — Calendar, Patients and Dashboard all render at `/` (state nav,
 * not URL), so the pathname alone would label the Patients list "Calendar".
 *
 * Earlier crumbs are real links (`<a href>`), but the click is handed to
 * `onNavigate` so the shell can run it through the unsaved-changes guard —
 * a plain router `<Link>` would leave a dirty patient edit page without the
 * discard confirm the rail already enforces.
 */

export interface Crumb {
  label: string;
  /** Present on every crumb but the last. */
  href?: string;
  /** A page-supplied trail's own click (it runs the page's guard); wins over `href`. */
  onClick?: () => void;
}

const ROOT_LABELS: Record<NavId, string> = {
  calendar: 'Calendar',
  dashboard: dashboardNavLabel(),
  patients: 'Patients',
  billing: 'Billing',
  reports: 'Reports',
  stock: 'Stock',
  studio: 'Studio',
};

/** First path segment → module label, for URL-derived modules. */
const MODULE_LABELS: Record<string, string> = {
  patients: 'Patients',
  billing: 'Billing',
  reports: 'Reports',
  stock: 'Stock',
  studio: 'Studio',
  settings: 'Settings',
};

/** A rule's parents: one crumb, or a trail of them (outermost first). */
type CrumbRule = [RegExp, string, (Crumb | Crumb[])?, 'own'?];
const MASTER_DATA_CRUMB: Crumb = { label: 'Master Data', href: '/billing/master-data' };
const PAYERS_CRUMB: Crumb = { label: 'Payers', href: '/billing/master-data/payers' };
const LABS_CRUMB: Crumb = { label: 'Labs', href: '/billing/master-data/labs' };
const BILLING_CRUMBS: CrumbRule[] = [
  [/^\/billing\/cashier-shift\/[^/]+/, 'Shift detail', { label: 'Cashier shift', href: '/billing/cashier-shift' }, 'own'],
  [/^\/billing\/cashier-shift\/?$/, 'Cashier shift'],
  [/^\/billing\/claims-for-submission\/[^/]+/, 'Batch details', { label: 'Claims for submission', href: '/billing/claims-for-submission' }],
  [/^\/billing\/claims-for-submission\/?$/, 'Claims for submission'],
  [/^\/billing\/claims-for-resubmission\/[^/]+/, 'Batch details', { label: 'Claims for resubmission', href: '/billing/claims-for-resubmission' }],
  [/^\/billing\/claims-for-resubmission\/?$/, 'Claims for resubmission'],
  [/^\/billing\/claim-workbench\/?$/, 'Claim workbench'],
  [/^\/billing\/remittance-advice\/?$/, 'Remittance advice'],
  [/^\/billing\/reconciliation\/?$/, 'Reconciliation'],
  // An invoice is a Billing record: its crumb is its place in the structure, not the page it was opened
  // from (Back walks the history). It opens from the patient file, never from the claim workbench
  // any more, so the old "Claim workbench" parent was wrong (PROD-3203 M1).
  [/^\/billing\/invoice\/[^/]+/, 'Invoice', undefined, 'own'],
  [/^\/billing\/master-data\/service-list\/[^/]+/, 'Service list detail', { label: 'Service list', href: '/billing/master-data/service-list' }, 'own'],
  [/^\/billing\/master-data\/service-list\/?$/, 'Service list'],
  [/^\/billing\/master-data\/provider-fees\/[^/]+/, 'Provider', { label: 'Provider fees', href: '/billing/master-data/provider-fees' }, 'own'],
  [/^\/billing\/master-data\/provider-fees\/?$/, 'Provider fees'],
  // Labs and Payers drill in (Ahmed, 28 Oct 2026): their tab row replaces the Master Data row, so the crumb
  // carries Master Data as the way back — Billing › Master Data › Labs › Tests & panels.
  [/^\/billing\/master-data\/payers\/corporate\/?$/, 'Corporate payers', [MASTER_DATA_CRUMB, PAYERS_CRUMB]],
  [/^\/billing\/master-data\/payers\/packages\/?$/, 'Packages', [MASTER_DATA_CRUMB, PAYERS_CRUMB]],
  [/^\/billing\/master-data\/payers\/membership\/?$/, 'Membership', [MASTER_DATA_CRUMB, PAYERS_CRUMB]],
  [/^\/billing\/master-data\/payers\/patient-packages\/?$/, 'Patient packages', [MASTER_DATA_CRUMB, PAYERS_CRUMB]],
  [/^\/billing\/master-data\/payers\/?$/, 'Insurance payers', [MASTER_DATA_CRUMB, PAYERS_CRUMB]],
  [/^\/billing\/master-data\/tpa\/?$/, 'TPA'],
  [/^\/billing\/master-data\/labs\/vendors\/?$/, 'Lab vendors', [MASTER_DATA_CRUMB, LABS_CRUMB]],
  [/^\/billing\/master-data\/labs(\/|$)/, 'Tests & panels', [MASTER_DATA_CRUMB, LABS_CRUMB]],
];
const SETTINGS_CRUMBS: Array<[RegExp, Crumb, string]> = [
  [/^\/settings\/practitioner\/[^/]+/, { label: 'Practitioner', href: '/settings/practitioner' }, 'Practitioner'],
  [/^\/settings\/staff\/[^/]+/, { label: 'Staff', href: '/settings/staff' }, 'Staff member'],
  [/^\/settings\/practices\/[^/]+/, { label: 'Practices', href: '/settings/practices' }, 'Practice'],
  [/^\/settings\/departments\/[^/]+/, { label: 'Departments', href: '/settings/departments' }, 'Department'],
  [/^\/settings\/resources\/[^/]+/, { label: 'Devices', href: '/settings/resources' }, 'Device'],
];

/** `own`: the page's own name for itself (a patient's name), set with `useSetBarCrumb`. `search`: the query string. */
export function breadcrumbsFor(pathname: string, activeNav: NavId, own: string | null = null, search = ''): Crumb[] {
  if (pathname === '/' || pathname === '') {
    return [{ label: ROOT_LABELS[activeNav] }];
  }
  if (pathname.startsWith('/patients/edit/')) {
    return [{ label: 'Patients', href: '/patients' }, { label: own ?? 'Patient' }];
  }
  if (pathname === '/patients/new') {
    return [{ label: 'Patients', href: '/patients' }, { label: 'New patient' }];
  }
  if (pathname.startsWith('/scheduling/appointment/')) {
    return [{ label: 'Calendar', href: '/' }, { label: own ?? 'Appointment' }];
  }
  if (/^\/stock\/items\/[^/]+/.test(pathname)) {
    return [{ label: 'Stock', href: '/stock/items' }, { label: 'Stock Item Enquiry', href: '/stock/items' }, { label: 'Stock movement' }];
  }
  // Billing's pages carry no in-page breadcrumb (Antonin, 13 Oct): the bar reads Billing › <page>, and a
  // drill-in adds its parent page (Billing › Cashier shift › <shift>). Most specific path first.
  const billing = BILLING_CRUMBS.find(([re]) => re.test(pathname));
  if (billing) {
    const [, label, parent, named] = billing;
    const parents = parent ? (Array.isArray(parent) ? parent : [parent]) : [];
    return [{ label: 'Billing', href: '/billing' }, ...parents, { label: (named && own) || label }];
  }
  // Lab settings opened from Labs (Settings › Clinic services marked `?from=labs`): lead back to Labs (Ahmed, 28 Oct).
  if (/^\/settings\/clinic-services\/?$/.test(pathname) && new URLSearchParams(search).get('from') === 'labs') {
    return [{ label: 'Billing', href: '/billing' }, MASTER_DATA_CRUMB, LABS_CRUMB, { label: 'Lab settings' }];
  }
  // Settings' full-page editors: Settings › <list> › <the record> (the page sets the record's name).
  if (/^\/settings\/account\/?$/.test(pathname)) return [{ label: 'Account settings' }];
  const settings = SETTINGS_CRUMBS.find(([re]) => re.test(pathname));
  if (settings) {
    const [, parent, label] = settings;
    return [{ label: 'Settings', href: '/settings' }, parent, { label: own ?? label }];
  }
  // A report names itself: Reports › the report (its page sets the name).
  if (/^\/reports\/.+/.test(pathname)) return [{ label: 'Reports', href: '/reports' }, { label: own ?? 'Report' }];
  const segment = pathname.split('/')[1] ?? '';
  const label = MODULE_LABELS[segment];
  return label ? [{ label }] : [{ label: ROOT_LABELS[activeNav] }];
}

/** The placeholder follows the page and says what the search finds from here (it always lands on the patient list). */
export function placeholderFor(pathname: string, activeNav: NavId): string {
  const section = pathname === '/' || pathname === '' ? activeNav : pathname.split('/')[1];
  if (section === 'patients') return 'Search patients by name, MRN or phone';
  if (section === 'dashboard') return 'Find the patient behind a conversation';
  if (section === 'billing') return "Find a patient's invoices and claims";
  if (section === 'calendar' || section === 'scheduling') return 'Find a patient to book or check in';
  return 'Find a patient';
}

interface TopBarProps {
  activeNav: NavId;
  /** Navigates to an earlier crumb; the shell wraps it in the nav guard. */
  onNavigate: (path: string, options?: NavigateOptions) => void;
  /** One step back in this tab's history; the shell wraps it in the nav guard. Without it, Back goes up a crumb. */
  onBack?: () => void;
}

export function TopBar({ activeNav, onNavigate, onBack }: TopBarProps) {
  const { pathname, search } = useLocation();
  const ownCrumb = useBarCrumb();
  // A page that hands over its whole trail (the Studio editors) wins over the trail derived from the
  // address (Antonin, 13 Oct: one breadcrumb, the top bar's).
  const trail = useBarTrail();
  const crumbs: Crumb[] = trail
    ? trail.map((t, i) =>
        i === trail.length - 1 ? { label: t.label } : { label: t.label, href: t.to ?? '#', onClick: t.onClick },
      )
    : breadcrumbsFor(pathname, activeNav, ownCrumb, search);
  // Back returns on sub-pages (Antonin, 19 Oct: "bring back a back button before the breadcrumbs"); main pages
  // (a lone crumb) still show nothing on the left. The routing-history stack (nav-stack.ts) keeps running, keyed by section.
  const lone = crumbs.length === 1 ? crumbs[0] : null;
  const sectionKey = pathname.startsWith(HOME) ? HOME : pathname === '/' ? `/${activeNav}` : '/' + (pathname.split('/')[1] ?? '');
  useNavStack(lone ? { key: sectionKey, to: pathname + search, label: lone.label } : null);
  const placeholder = placeholderFor(pathname, activeNav);
  // The practice switcher, top right after the search (PRACTICE_IN_BAR). Same menu as it had beside "Calendar"; the
  // settings rows only for roles that can open that page (PROD-2396).
  const organizationId = useAuthStore((s) => s.user?.organizationId);
  const canOpenPracticeSettings = useAuthStore((s) => !isNavConfined(s.user) && !isPhlebotomistRole(s.user));
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Back before the crumbs on a sub-page (Antonin, 19 Oct: "bring back the back button, before the breadcrumbs"):
  // the page this tab came from; opened directly (no earlier page in the tab), the parent crumb.
  const parent = [...crumbs].reverse().find((c) => c.href);
  const handleBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (onBack && idx > 0) onBack();
    else if (parent?.onClick) parent.onClick();
    else if (parent?.href) onNavigate(parent.href);
  };

  const handleCrumbClick = (event: MouseEvent<HTMLAnchorElement>, crumb: Crumb) => {
    // Let modified clicks (new tab / window) keep native link behaviour.
    if (crumb.href !== '#' && (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)) return;
    event.preventDefault();
    if (crumb.onClick) crumb.onClick();
    else if (crumb.href) onNavigate(crumb.href);
  };

  return (
    <header
      data-global-bar
      className="flex h-[var(--zone-global-bar)] flex-shrink-0 items-center justify-between gap-3 pr-[var(--zone-frame-inset-right)]"
      data-testid="app-top-bar"
    >
      {/* A main page shows nothing here (Antonin, 13 Oct); the page title already names it. Sub-pages
          show Back, then their crumbs. */}
      {lone ? <div className="flex-1" /> : (
      // `contents`: Back and the crumbs stay items of the bar, so the crumbs' width cap (showcase-bridge.css) still
      // measures the bar, not a wrapper that shrinks to nothing. The bar's 12 gap sits between Back and the crumbs; -mr-1 on Back makes it 8.
      <div className="contents">
      <div data-bar-back className="-mr-1 flex shrink-0 items-center">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={handleBack} data-history-back className="text-[var(--ink-secondary)] hover:bg-[var(--surface-read)] hover:text-[var(--ink-body)]">
          <ArrowLeft className="size-5" aria-hidden />
        </Button>
      </div>
      <nav aria-label="Breadcrumb" data-bar-breadcrumb className="min-w-0">
        <ol className="flex items-center gap-2 [font:var(--type-body-strong)]">
          {crumbs.map((crumb, index) => (
            <li key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-2">
              {index > 0 ? (
                <span aria-hidden className="text-[var(--ink-subtle)]">
                  ›
                </span>
              ) : null}
              {crumb.href ? (
                <a
                  href={crumb.href}
                  onClick={(event) => handleCrumbClick(event, crumb)}
                  className="truncate text-[var(--ink-secondary)] hover:text-[var(--ink-body)] focus-visible:text-[var(--ink-body)]"
                >
                  {crumb.label}
                </a>
              ) : (
                <span aria-current="page" className="truncate text-[var(--ink-body)]">
                  {crumb.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
      </div>
      )}
      <div data-testid="app-top-bar-actions" className="flex min-w-0 flex-1 items-center justify-end gap-3">
        <ReleaseTourHelpMenu />
        <form
          role="search"
          className="min-w-[var(--search-min-width)] flex-[0_100_var(--search-width)]"
          onSubmit={(e) => {
            e.preventDefault();
            const term = query.trim();
            // The term rides in navigation state, never the address: a patient's name, MRN or phone must not
            // land in the URL (history, logs, shared links).
            onNavigate('/patients', term ? { state: { search: term } } : undefined);
            setQuery('');
            inputRef.current?.blur();
          }}
        >
          <label className="flex h-8 items-center gap-2 rounded-input border border-[var(--surface-line)] bg-[var(--surface-read)] pl-3 pr-2 focus-within:border-[var(--accent-primary)]">
            <Search className="size-4 shrink-0 text-[var(--ink-secondary)]" aria-hidden />
            {/* The placeholder as a text layer, so it can swap under a swipe of light (showcase-bridge.css);
                the input keeps its real placeholder, transparent, for assistive tech. */}
            <span className="relative flex min-w-0 flex-1 items-center">
              {!query && (
                <span aria-hidden className="search-ph text-sm">
                  <span key={placeholder}>{placeholder}</span>
                </span>
              )}
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search patients"
                placeholder={placeholder}
                className="relative min-w-0 flex-1 border-0 bg-transparent text-sm text-[var(--ink-body)] outline-none placeholder:text-transparent"
              />
            </span>
            <kbd className="shrink-0 rounded-tag border border-[var(--surface-line)] px-1 font-sans text-xs font-normal text-[var(--color-text-hint)]">
              ⌘K
            </kbd>
          </label>
        </form>
        {PRACTICE_IN_BAR && organizationId ? (
          <PracticeSwitcher
            organizationId={organizationId}
            className="h-8 shrink-0"
            onOpenSettings={canOpenPracticeSettings
              // Through onNavigate so the unsaved-changes guard runs like every other bar navigation. `from`: the settings page's Back and save-return land on the page you opened it from.
              ? (id) => onNavigate(`/settings/practices/${id}`, { state: { from: pathname + search } })
              : undefined}
          />
        ) : null}
      </div>
    </header>
  );
}
